import { css } from '@emotion/css';
import { ActionIcon, Loader, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowUp, IconSparkles } from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales';
import posthog from 'posthog-js';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { captureEvent } from 'shared/analytics/capture-event';
import { useGenerateDraft } from '../hooks/use-generate-draft';

const gradientPlaceholderClass = css`
  &::placeholder {
    background: linear-gradient(135deg, #5b21b6, #ec4899, #a78bfa);
    background-clip: text;
    -webkit-background-clip: text;
    color: transparent;
    opacity: 0.6;
  }
`;

type MailComposerAiPromptProps = {
  threadId: string;
  onGenerated: (html: string) => void;
  focusSignal?: number;
};

export const MailComposerAiPrompt = ({
  threadId,
  onGenerated,
  focusSignal,
}: MailComposerAiPromptProps) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const { generateDraft, isGenerateDraftLoading } = useGenerateDraft();
  const inputRef = useRef<HTMLInputElement>(null);

  const isPromptEmpty = !prompt.trim();

  useEffect(() => {
    if (focusSignal) inputRef.current?.focus();
  }, [focusSignal]);

  const handleSubmit = () => {
    if (isPromptEmpty || isGenerateDraftLoading) return;

    generateDraft(
      { threadId, userPrompt: prompt },
      {
        onSuccess: data => {
          captureEvent(posthog, 'mail:ai_draft_generated', {
            status: data.status,
            prompt_length: prompt.length,
          });

          if (data.status === 'error') {
            notifications.show({
              color: 'red',
              title: t(i18nKeys.EXTERNAL_MAIL.GENERATE_REPLY_MODAL.ERROR_TITLE),
              message: data.body,
            });
            return;
          }

          onGenerated(data.body);
          setPrompt('');
        },
      },
    );
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    handleSubmit();
  };

  return (
    <TextInput
      ref={inputRef}
      w={340}
      value={prompt}
      onChange={event => setPrompt(event.currentTarget.value)}
      onKeyDown={handleKeyDown}
      placeholder={t(i18nKeys.EXTERNAL_MAIL.GENERATE_REPLY_MODAL.PROMPT_PLACEHOLDER)}
      disabled={isGenerateDraftLoading}
      classNames={{ input: gradientPlaceholderClass }}
      leftSection={<IconSparkles size={16} color="var(--mantine-color-violet-5)" />}
      rightSection={
        isGenerateDraftLoading ? (
          <Loader size="xs" color="violet" />
        ) : (
          <ActionIcon
            variant="gradient"
            gradient={{ from: 'violet.7', to: 'pink.5', deg: 135 }}
            radius="xl"
            aria-label={t(i18nKeys.EXTERNAL_MAIL.GENERATE_REPLY_MODAL.GENERATE)}
            disabled={isPromptEmpty}
            onClick={handleSubmit}
          >
            <IconArrowUp size={16} />
          </ActionIcon>
        )
      }
    />
  );
};
